import type { Catalog } from '../catalog';

export const en: Catalog = {
  app: {
    name: 'worldSim2',
    tagline: 'Emergent history simulator',
  },
  placeholder: {
    title: 'The house is ready',
    body: 'Phase 0: architecture foundations. There is no world to watch yet.',
    engineVersion: 'Engine v{{version}}',
  },
  map: {
    status: {
      starting: 'Preparing the map…',
      generating: 'Generating the world: {{stage}} ({{index}} of {{total}})',
      ready:
        'Seed {{seed}} · {{size}} world · {{provinces}} provinces · {{backend}} · ready in {{seconds}} s',
      error: 'Could not show the map: {{message}}',
    },
    stage: {
      plates: 'plates',
      relief: 'relief',
      erosion: 'erosion',
      climate: 'climate',
      hydrology: 'rivers and lakes',
      biomes: 'biomes',
      provinces: 'provinces',
      seas: 'seas',
      graph: 'routes',
      hash: 'verification',
    },
    backend: {
      webgpu: 'WebGPU',
      webgl2: 'WebGL2',
      webgl2Forced: 'WebGL2 (forced)',
    },
    colors: {
      label: 'Fill',
      political: 'Political',
      biome: 'Dominant biome',
      id: 'One color per province',
      none: 'No fill',
    },
    hover: {
      province: 'Province {{id}}',
      area: '{{area}} km²',
      ocean: 'Ocean',
      lake: 'Lake',
    },
    selected: 'Selected: province {{id}}',
    help: 'Drag to move · wheel to zoom · Q and E to rotate · click to select',
  },
  live: {
    status: {
      starting: 'Preparing the simulation…',
      year: '{{year}} · {{era}}',
      waiting: 'waiting for the simulation',
      ahead: 'simulated up to {{year}}',
      finished: 'end of the run',
      viewOnly: 'History saved with engine version {{version}}: it can be viewed, not continued',
      cost: '{{ms}} ms per simulated year',
      error: 'The simulation failed: {{message}}',
    },
    entity: {
      none: 'unclaimed land',
      warBetween: 'war between {{attacker}} and {{defender}}',
      fallback: {
        province: 'province {{id}}',
        settlement: 'settlement {{id}}',
        polity: 'polity {{id}}',
        person: 'person {{id}}',
        culture: 'culture {{id}}',
        war: 'war {{id}}',
      },
    },
    speed: {
      label: 'Speed',
      pause: 'Pause',
      x1: '1×',
      x2: '2×',
      x4: '4×',
      max: 'Max',
    },
    timeline: {
      label: 'Timeline: click or drag to go to another year',
    },
    saves: {
      save: 'Save',
      saving: 'Saving…',
      saved: 'Saved up to {{year}}',
      title: 'Saved worlds',
      empty: 'No saved worlds yet.',
      item: 'Seed {{seed}} · {{size}} · up to {{year}}',
      load: 'Load',
      remove: 'Delete',
      loading: 'Loading the saved world…',
      notFound: 'Saved world {{id}} not found.',
      error: 'Could not save: {{message}}',
    },
  },
  ui: {
    create: {
      title: 'Create world',
      params: 'World parameters',
      seed: 'Seed',
      seedHint: 'number or text',
      random: 'Random',
      size: 'Size',
      sizeHint: '{{provinces}} provinces',
      mapType: 'Map type',
      land: 'Land fraction',
      landHint: 'The default depends on the map type.',
      generate: 'Generate world',
      generating: 'Generating: {{stage}} · stage {{index}} of {{total}}',
      error: 'Could not generate the world: {{message}}',
      retry: 'Retry',
    },
    saves: {
      title: 'Saved worlds (this browser)',
      empty: 'No saved worlds yet.',
      world: 'Seed {{seed}} · {{size}} · {{mapType}}',
      worldColumn: 'World',
      until: 'Up to',
      savedAt: 'Saved',
      load: 'Load',
      export: 'Export',
      remove: 'Delete',
      import: 'Import .wsim…',
      importError: 'Could not import: {{message}}',
      viewOnly: 'view only (engine {{version}})',
    },
    world: {
      label: 'Seed {{seed}} · {{size}} · {{mapType}}',
      summary: 'Summary',
      export: 'Export',
      back: 'Back to the live view',
      generating: 'Generating the world: {{stage}} ({{index}} of {{total}})',
      preparingMap: 'Preparing the map…',
      mapError: 'Could not show the map: {{message}}. The simulation keeps running.',
      error: 'Could not open the world: {{message}}',
      retry: 'Retry',
      create: 'Create another world',
      shortcuts: 'Shortcuts: space pause · ←/→ ±100 years · 1–4 speeds · Esc closes the inspector',
    },
    share: {
      button: 'Share',
      title: 'Share this world',
      hint: 'The link regenerates the same world and its history with the same engine version ({{version}}).',
      copy: 'Copy',
      copied: 'Link copied',
      copyFailed: 'Could not copy: select the link and copy it by hand.',
      close: 'Close',
      worldHash: 'World fingerprint',
      finalHash: 'Final fingerprint ({{year}})',
      computeFinal: 'Compute the final fingerprint',
      notFinished: 'The run is not over yet: the final fingerprint appears at 1900.',
    },
    shared: {
      running: 'Simulating the linked history: {{year}} · {{pct}} %',
      done: 'History ready: {{years}} years simulated.',
      play: 'Play from the beginning',
      finalHash: 'Final fingerprint: {{hash}}',
      versionMismatch:
        'This link comes from engine version {{link}} and this is {{engine}}: the world and its history may differ.',
    },
    rail: {
      mapMode: 'Map mode',
      legend: 'Legend',
      legendPolity: 'one color per polity',
      legendNoOwner: 'no color: unclaimed',
      legendSettlements: 'dots: village → metropolis',
    },
    inspector: {
      title: 'Inspector',
      atDate: 'as of {{year}}',
      empty: 'Click the map or an event to inspect.',
      close: 'Close',
      back: 'Back',
      open: 'Open card',
      notYet: 'Does not exist yet at this date.',
      owner: 'Owner',
      settlement: 'Settlement',
      population: 'Population',
      urban: 'urban {{pct}} %',
      culture: 'Majority culture',
      knows: 'Knows',
      nothing: 'nothing yet',
      founded: 'Founded',
      ended: 'Ended',
      active: 'active',
      endReason: {
        active: 'active',
        absorbed: 'absorbed by another polity',
        conquered: 'conquered',
      },
      capital: 'Capital',
      provinces: 'Provinces',
      ruler: 'Ruler',
      heir: 'Heir',
      rulers: 'Rulers',
      wars: 'Wars',
      born: 'Born',
      died: 'Died',
      alive: 'alive',
      age: '{{age}} years old',
      parents: 'Parents',
      ruled: 'Ruled',
      language: 'Language',
      origin: 'Origin',
      majority: 'Majority in {{count}} provinces',
      attacker: 'Attacker',
      defender: 'Defender',
      started: 'Started',
      outcome: 'Outcome',
      level: 'Level',
      province: 'Province',
      none: '—',
      percent: '{{value}} %',
      more: '+{{count}} earlier',
      warItem: '{{opponent}} ({{year}})',
      show: 'Show',
      showAtDate: 'as of ({{year}})',
      showFinal: 'at the end',
      finalOnlyWhenDone: '(only when the run is over)',
      events: 'Events (with their causes)',
      noEvents: 'No events up to this date.',
      cause: 'because: {{text}}',
    },
    why: {
      title: 'Why? (exact state at this date)',
      compute: 'Compute why',
      computing: 'Computing… (re-simulating from the last keyframe)',
      at: 'computed for {{year}} in {{ms}} ms',
      refresh: 'Update to {{year}}',
      base: 'base',
      value: 'value',
      modifier: 'modifier ({{source}})',
      cropShare: 'Cultivated land',
      foodSecurity: 'Food / need',
      cultures: 'Population by culture',
      notAvailable: 'This history comes from another engine version: the why cannot be recomputed.',
      error: 'Could not compute: {{message}}',
    },
    stat: {
      cropYield: 'Crop yield',
      forageYield: 'Foraging yield',
      cropReach: 'Reachable arable land',
      diffusionRate: 'Knowledge diffusion',
      urbanShare: 'Base urban share',
      combatStrength: 'Military strength',
      reach: 'Administrative reach',
    },
    unit: {
      tons_per_year: 't per km² per year',
      per_year: 'per year',
      fraction: 'share',
      score: 'points',
      days: 'days',
    },
    kind: {
      province: 'Province',
      settlement: 'Settlement',
      polity: 'Polity',
      person: 'Character',
      culture: 'Culture',
      war: 'War',
    },
    kinds: {
      polity: 'Polities',
      settlement: 'Settlements',
      culture: 'Cultures',
      person: 'Characters',
      war: 'Wars',
    },
    outcome: {
      ongoing: 'ongoing',
      attackerVictory: 'attacker victory',
      defenderVictory: 'defender victory',
      whitePeace: 'white peace',
    },
    ticker: {
      title: 'Ticker',
      empty: 'Nothing important has happened yet.',
    },
    summary: {
      until: 'Summary up to {{year}} (what was seen)',
      final: 'Final overview',
      population: 'Population',
      polities: 'Active polities',
      foundedTotal: 'founded: {{count}}',
      wars: 'Wars',
      settlements: 'Settlements',
      era: 'Dominant era',
      chartPopulation: 'World population',
      chartPolities: 'Number of polities',
      chartLargest: 'Largest polity (people)',
      empty: 'Nothing up to this date.',
      rows: '{{count}} rows',
    },
    column: {
      name: 'Name',
      founded: 'Founded',
      ended: 'Ended',
      provinces: 'Provinces',
      population: 'Population',
      capital: 'Capital',
      province: 'Province',
      level: 'Level',
      language: 'Language',
      majority: 'Majority provinces',
      born: 'Born',
      died: 'Died',
      ruled: 'Ruled',
      attacker: 'Attacker',
      defender: 'Defender',
      started: 'Started',
      outcome: 'Outcome',
    },
  },
  time: {
    yearBC: '{{year}} BC',
    yearAD: '{{year}} AD',
  },
  language: {
    label: 'Language',
    es: 'Español',
    en: 'English',
  },
  events: {
    territory: {
      changed: '{{province}} passes from {{from}} to {{to}}',
    },
    subsistence: {
      shifted: 'In {{province}} farming now feeds most people',
    },
    famine: {
      started: 'Famine in {{province}}',
      ended: 'The famine in {{province}} ends',
    },
    innovation: {
      invented: '{{innovation}} emerges in {{province}}',
    },
    settlement: {
      founded: '{{settlement}} is founded in {{province}}',
      grew: '{{settlement}} grows into a {{level}}',
      declined: '{{settlement}} declines into a {{level}}',
    },
    polity: {
      formed: '{{polity}} arises in {{province}}',
      absorbed: '{{polity}} joins {{by}}',
      collapsed: '{{polity}} vanishes',
    },
    person: {
      died: '{{person}} dies aged {{age}}',
    },
    ruler: {
      succeeded: '{{person}} rules {{polity}}',
    },
    war: {
      declared: '{{attacker}} declares war on {{defender}}',
      ended: 'The war between {{attacker}} and {{defender}} ends',
    },
    treaty: {
      signed: 'Peace between {{attacker}} and {{defender}}',
    },
    battle: {
      fought: 'Battle in {{province}} between {{attacker}} and {{defender}}',
    },
  },
  content: {
    worldSize: { small: 'Small', medium: 'Medium' },
    mapType: { continents: 'Continents', pangaea: 'Pangaea', archipelago: 'Archipelago' },
    biome: {
      ice: 'Ice',
      alpine: 'Alpine',
      tundra: 'Tundra',
      taiga: 'Taiga',
      desert: 'Desert',
      grassland: 'Grassland',
      temperateForest: 'Temperate forest',
      savanna: 'Savanna',
      tropicalForest: 'Tropical forest',
    },
    settlementLevel: {
      village: 'Village',
      town: 'Town',
      city: 'City',
      greatCity: 'Great city',
      metropolis: 'Metropolis',
    },
    innovation: {
      agriculture: 'Agriculture',
      pastoralism: 'Pastoralism',
      pottery: 'Pottery',
      wheel: 'Wheel',
      copper: 'Copper metallurgy',
      plough: 'Plough',
      writing: 'Writing',
      bronze: 'Bronze',
      iron: 'Iron',
      coinage: 'Coinage',
      cropRotation: 'Crop rotation',
      oceanNavigation: 'Ocean navigation',
      printing: 'Printing press',
      gunpowder: 'Gunpowder',
    },
    era: {
      prehistory: 'Prehistory',
      neolithic: 'Neolithic',
      bronzeAge: 'Bronze Age',
      ironAge: 'Iron Age',
      earlyModern: 'Early modern age',
    },
    stratum: { common: 'Commoners' },
    law: { primogeniture: 'Primogeniture' },
  },
};
